import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { ListRow } from "components/designV2/listRow/listRow";
import { LabeledValue } from "components/designV2/labeledValue/labeledValue";
import { ColorV2 } from "config/colors";
import { CustomIcon } from "components/icon/customIcon.component";
import { AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { Api } from "services/api/api.service";
import { LocalDate } from "@js-joda/core";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import { LoadMoreSentinel } from "./salary.styled";
import { AbsenceDetailRow } from "./absenceDetailRow.component";
import { PayoutDetailRow } from "./payoutDetailRow.component";
import React from "react";
import { workersAGI } from "model/Salary";
import {
  ClickableRow,
  ExpandedSection,
  ListContainer,
  RowOuter,
} from "./agiList.styled";

interface AGIListProps {
  currentDate: LocalDate;
  data: workersAGI[];
  setData: React.Dispatch<React.SetStateAction<workersAGI[]>>;
}

export interface DetailsState {
  isOpen: boolean;
  selectedWorkerId: number | null;
}

const ITEMS_PER_PAGE = 25;

export const AGIList: React.FC<AGIListProps> = (props) => {
  const { t } = useTranslation();
  const [{ company, companyWorkerImages, workerIdToExternalId }] =
    useCompanyStore();
  const [allWorkers, setAllWorkers] = useState<workersAGI[]>([]);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [detailsState, setDetailsState] = useState<DetailsState>({
    isOpen: false,
    selectedWorkerId: null,
  });
  const currency = " SEK";

  const fetchAGIData = useCallback(() => {
    if (!company?.id) return;

    Api()
      .company.salary.getWorkersAGI(
        company.id,
        props.currentDate.year(),
        props.currentDate.monthValue(),
      )
      .then((res) => {
        setAllWorkers(res.data.workers);
        setVisibleCount(ITEMS_PER_PAGE);
        setDetailsState({ isOpen: false, selectedWorkerId: null });
      })
      .catch((e: AxiosError) => {
        console.error("Error when getting individual agi", e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, props.currentDate]);

  const hasMore = visibleCount < allWorkers.length;

  useEffect(() => {
    props.setData(allWorkers.slice(0, visibleCount));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allWorkers, visibleCount, props.setData]);

  const handleLoadMore = useCallback(() => {
    setVisibleCount((previousCount) => previousCount + ITEMS_PER_PAGE);
  }, []);

  const { loadMoreRef } = useInfiniteScroll({
    hasMore,
    isLoading: false,
    itemCount: visibleCount,
    onLoadMore: handleLoadMore,
  });

  const toggleDetails = useCallback((workerId: number) => {
    setDetailsState((prev) => ({
      isOpen: prev.selectedWorkerId === workerId ? !prev.isOpen : true,
      selectedWorkerId: workerId,
    }));
  }, []);

  const isDetailsVisible = useCallback(
    (workerId: number) =>
      detailsState.isOpen && detailsState.selectedWorkerId === workerId,
    [detailsState],
  );

  useEffect(() => {
    fetchAGIData();
  }, [fetchAGIData]);

  const renderAGIRow = useCallback(
    (agi: workersAGI) => {
      const detailsOpen = isDetailsVisible(agi.workerId);
      const externalId = workerIdToExternalId[agi.workerId];
      const avatarUrl = externalId
        ? (companyWorkerImages[externalId] ?? null)
        : null;

      return (
        <ListRow key={`agi-worker-${agi.workerId}`}>
          <RowOuter>
            <ClickableRow onClick={() => toggleDetails(agi.workerId)}>
              <AvatarChip
                title={`${agi.firstName} ${agi.lastName}`}
                avatarUrl={avatarUrl}
              />
              <LabeledValue
                value={agi.personalIdentityNumber}
                label={t("Salary.PersonalIdentityNumber")}
                variant="small"
              />
              <LabeledValue
                value={`${agi.totalGrossPayoutAmount}${currency}`}
                label={t("Salary.GrossPayoutAmount")}
                variant="small"
              />
              <LabeledValue
                value={`${agi.totalEmployerTax}${currency}`}
                label={t("Salary.EmployerTax")}
                variant="small"
              />
              <LabeledValue
                value={`${agi.totalWorkerTax}${currency}`}
                label={t("Salary.Tax")}
                variant="small"
              />
              <CustomIcon
                name={detailsOpen ? "v2/up-chevron" : "v2/down-chevron"}
                size="20px"
                color={ColorV2.SimflexityGreen}
              />
            </ClickableRow>

            {detailsOpen && (
              <ExpandedSection>
                <PayoutDetailRow
                  payoutDetails={agi.payoutDetails}
                  currency="SEK"
                />
                {agi.absences.length > 0 && (
                  <AbsenceDetailRow absences={agi.absences} />
                )}
              </ExpandedSection>
            )}
          </RowOuter>
        </ListRow>
      );
    },
    [
      t,
      currency,
      toggleDetails,
      isDetailsVisible,
      companyWorkerImages,
      workerIdToExternalId,
    ],
  );

  return (
    <ListContainer>
      {props.data.map(renderAGIRow)}
      {hasMore && <LoadMoreSentinel ref={loadMoreRef} />}
    </ListContainer>
  );
};
